import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

// The UI kit's badges: a tinted pill with a dark text of the same hue.
// `slate` is the neutral state (an option), `gray` a draft.
export const badgeVariants = cva(
  'inline-flex items-center whitespace-nowrap font-semibold',
  {
    variants: {
      size: {
        sm: 'h-5 gap-1 px-2 text-[11px]',
        md: 'h-[26px] gap-1.5 px-2.5 text-xs',
      },
      /** pill for status badges; square sits in toolbars next to buttons */
      shape: {
        pill: 'rounded-full',
        square: 'rounded-md',
      },
      color: {
        gray: 'border border-solid border-k-gray bg-navy-50 text-k-p',
        green: 'bg-green-100 text-green-800',
        blue: 'bg-blue-100 text-blue-800',
        amber: 'bg-amber-100 text-amber-800',
        orange: 'bg-orange-50 text-[#9A4A00]',
        red: 'bg-red-100 text-red-800',
        purple: 'bg-purple-100 text-purple-800',
        slate: 'bg-navy-100 text-k-h',
      },
    },
    defaultVariants: {
      color: 'gray',
      size: 'md',
      shape: 'pill',
    },
  },
);

const dotVariants = cva('h-1.5 w-1.5 shrink-0 rounded-full', {
  variants: {
    color: {
      gray: 'bg-navy-400',
      green: 'bg-green-600',
      blue: 'bg-blue-600',
      amber: 'bg-amber-500',
      orange: 'bg-k-orange',
      red: 'bg-red-600',
      purple: 'bg-purple-600',
      slate: 'bg-k-h',
    },
  },
  defaultVariants: {
    color: 'gray',
  },
});

type KoobBadgeProps = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'className' | 'color'
> &
  VariantProps<typeof badgeVariants> & {
    /** Leading status dot, for state badges (confirmed, pending, …). */
    dot?: boolean;
  };

const KoobBadge = React.forwardRef<HTMLSpanElement, KoobBadgeProps>(
  ({ color, size, shape, dot = false, children, ...props }, ref) => (
    <span
      ref={ref}
      className={badgeVariants({ color, size, shape })}
      {...props}
    >
      {dot && <span className={dotVariants({ color })} />}
      {children}
    </span>
  ),
);

KoobBadge.displayName = 'KoobBadge';

export default KoobBadge;
